import { useState, type FormEvent } from 'react';
import { toUserMessage } from '@platform/api-client';

import { Alert, FormField } from '@/shared/ui';
import { useOnboardingLoginId } from '../../api/use-auth.mutation';
import { loginIdField } from '../../schemas/auth.schemas';
import { PrimaryButton, StepHeading } from './OnboardingUi';

/**
 * 계정 재설정 — 구버전 아이디가 신 규칙에 맞게 바뀌었거나 예약어인 이관 계정만 거친다.
 * 바뀐 아이디를 그대로 확정하거나 새로 정한다. 예약어 판정은 서버가 한다(가입과 같은 규칙).
 */
export function LoginIdResetStep({
  currentLoginId,
}: {
  currentLoginId: string;
}) {
  const [draft, setDraft] = useState(currentLoginId);
  const [draftError, setDraftError] = useState<string>();
  const save = useOnboardingLoginId();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = loginIdField.safeParse(draft.trim());
    if (!parsed.success) {
      setDraftError(parsed.error.issues[0]?.message);
      return;
    }
    setDraftError(undefined);
    save.mutate(parsed.data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <StepHeading title="아이디를 다시 정해 주세요">
        새 서비스의 아이디 규칙에 맞춰 예전 아이디를 아래처럼 바꿔 두었습니다.
        그대로 쓰거나 원하는 아이디로 바꿀 수 있습니다.
      </StepHeading>
      <FormField
        label="아이디"
        name="loginId"
        autoComplete="username"
        autoCapitalize="none"
        spellCheck={false}
        autoFocus
        required
        maxLength={20}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value.toLowerCase());
          setDraftError(undefined);
          if (save.error) save.reset();
        }}
        error={draftError}
        hint="영문 소문자·숫자·_ 4~20자. admin·system·test 처럼 운영자나 테스트 계정으로 오해될 단어는 쓸 수 없습니다."
      />
      {save.error && <Alert tone="error">{toUserMessage(save.error)}</Alert>}
      <PrimaryButton type="submit" disabled={save.isPending}>
        {save.isPending ? '저장하는 중…' : '이 아이디로 확정'}
      </PrimaryButton>
      <p className="break-keep text-xs text-neutral-600 dark:text-neutral-400">
        확정한 뒤에도 예전 아이디로 로그인할 수 있습니다.
      </p>
    </form>
  );
}
